import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiGet } from "../../../api/apiGet";
import { filterObject } from "../../../api/models/FilterModel";
import { apiPost } from "../../../api/apiPost";

export const getInitialProductList = createAsyncThunk(
  "ProductFinder/getInitialProductList",
  async (_, thunkAPI) => {
    try {
      const response = await apiGet.getInitialProducts();
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  }
);

export const searchProductList = createAsyncThunk(
  "ProductFinder/searchProductList",
  async (
    {
      product_code,
      available_only,
      search_xref,
      search_description,
      search_product_code,
      search_oem_part_numbers,
    },
    thunkAPI
  ) => {
    try {
      const state = thunkAPI.getState();
      const {
        productFinder: { page, filterObject, productCategories },
      } = state;

      const response = await apiPost.searchProductList({
        product_code,
        available_only,
        search_xref,
        search_description,
        search_product_code,
        search_oem_part_numbers,
        page,
        filterObject,
        productCategories,
      });
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  }
);

export const getProductById = createAsyncThunk(
  "ProductFinder/getProductById",
  async ({ product_code }, thunkAPI) => {
    try {
      const response = await apiGet.getProductById({ product_code });
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  }
);

export const getProductListRowDetails = createAsyncThunk(
  "ProductFinder/getProductListRowDetails",
  async ({ product_code }, thunkAPI) => {
    try {
      const response = await apiGet.getProductListRowDetails({ product_code });
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  }
);

export const getTrackMakesAndModels = createAsyncThunk(
  "ProductFinder/getTrackMakesAndModels",
  async ({ product_code }, thunkAPI) => {
    try {
      const response = await apiGet.getTrackMakesAndModels({ product_code });
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  }
);

export const getProductTypes = createAsyncThunk(
  "ProductFinder/getProductTypes",
  async (_, thunkAPI) => {
    try {
      const response = await apiGet.getProductFilterTypes();
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  }
);

export const getProductManufacturers = createAsyncThunk(
  "ProductFinder/getProductManufacturers",
  async (_, thunkAPI) => {
    try {
      const storeStates = thunkAPI.getState();
      const {
        productFinder: {
          filterObject: { types },
        },
      } = storeStates;

      const response = await apiPost.getProductManufacturers({ types });
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  }
);

export const getProductModels = createAsyncThunk(
  "ProductFinder/getProductModels",
  async (_, thunkAPI) => {
    try {
      const storeStates = thunkAPI.getState();
      const {
        productFinder: {
          filterObject: { manufacturers, types },
        },
      } = storeStates;

      const response = await apiPost.getProductModels({ manufacturers, types });
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  }
);

const initialState = {
  // This is kind of messy? We may move this to a model file at some point, or even to the api
  popupProduct: {},
  productList: [],
  initialList: undefined,
  productTypes: [],
  selectedCategories: [],
  allCategories: [],
  filterObject: { ...filterObject },
  productLoading: false,
  loading: false,
  page: 1,
  manufacturers: [],
  models: [],
  trackMatchProducts: [],
  steps: [
    {
      id: "Step 1",
      name: "Pick a Product",
      href: "#",
      status: "current",
      value: "",
      btn: ["next"],
    },
    {
      id: "Step 2",
      name: "Pick a Make",
      href: "#",
      status: "upcoming",
      value: "",
      btn: ["next", "back"],
    },
    {
      id: "Step 3",
      name: "Pick a Model",
      href: "#",
      status: "upcoming",
      value: "",
      btn: ["back", "submit"],
    },
  ],
  currentId: "Step 1",
};

export const productFinderSlice = createSlice({
  name: "productFinderSlice",
  initialState: initialState,
  reducers: {
    setTrackMatches: (state, action) => {
      state.trackMatchProducts = action.payload;
    },
    resetFilters: (state, action) => {
      state.filterObject = filterObject;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    updateStepField: (state, action) => {
      const id = action.payload.id;
      const value = action.payload.value;

      state.steps = state.steps.map((step) =>
        step.id === id ? { ...step, value: value } : step
      );
    },
    nextStep: (state, action) => {
      const curr_step = state.steps.find((step) => step.id === state.currentId);
      const curr_index = state.steps.indexOf(curr_step);

      state.steps[curr_index].status = "complete";
      state.steps[curr_index + 1].status = "current";
      state.currentId = state.steps[curr_index + 1].id;
    },
    backStep: (state, action) => {
      const curr_step = state.steps.find((step) => step.id === state.currentId);
      const curr_index = state.steps.indexOf(curr_step);

      state.steps[curr_index].status = "upcoming";
      state.steps[curr_index - 1].status = "current";
      state.currentId = state.steps[curr_index - 1].id;
    },
    updateFilter: (state, action) => {
      state.filterObject = action.payload;
      state.productCategories = action.payload.productCategories;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProductListRowDetails.fulfilled, (state, action) => {
        state.productList = state.productList.map((product) => {
          if (product.product_code === action.payload.product_code) {
            return {
              ...product,
              ...action.payload, // This overrides with new values from the action
            };
          }
          return product; // No need to clone if you're not modifying it
        });
      })
      .addCase(getInitialProductList.fulfilled, (state, action) => {
        state.productList = action.payload;
        state.initialList = action.payload;
        state.productLoading = false;
      })
      .addCase(getInitialProductList.rejected, (state, action) => {
        state.productLoading = false;
        // Latch the failure. ProductList only fetches when initialList is
        // falsy, so leaving it undefined here means every remount re-fires the
        // request — an unbounded loop against an endpoint that is already
        // failing. An empty list renders the same "no products" skeleton
        // without asking the server again.
        state.initialList = [];
      })
      .addCase(getInitialProductList.pending, (state, action) => {
        state.productLoading = true;
      })
      .addCase(getTrackMakesAndModels.fulfilled, (state, action) => {
        state.trackMatchProducts = action.payload;
      })

      .addCase(searchProductList.fulfilled, (state, action) => {
        state.productList = action.payload;
        state.pending = false;
      })
      .addCase(searchProductList.pending, (state, action) => {
        state.pending = true;
      })
      .addCase(searchProductList.rejected, (state, action) => {
        state.pending = false;
      })
      .addCase(getProductById.fulfilled, (state, action) => {
        const loadedProduct = state.productList.find(
          (product) => product.product_code === action.payload.product_code
        );
        state.popupProduct = {
          ...action.payload,
          price: loadedProduct?.price || 0,
          date_ordered: loadedProduct?.date_ordered || null,
        };
      })
      .addCase(getProductTypes.fulfilled, (state, action) => {
        state.productTypes = action.payload.types;
        state.productCategories = action.payload.selected_categories;
        state.allCategories = action.payload.all_categories;
      })
      .addCase(getProductManufacturers.fulfilled, (state, action) => {
        state.manufacturers = action.payload;
      })
      .addCase(getProductModels.fulfilled, (state, action) => {
        state.models = action.payload;
      });
  },
});

export const {
  updateStepField,
  nextStep,
  backStep,
  setPage,
  updateFilter,
  resetFilters,
  setTrackMatches,
} = productFinderSlice.actions;

export default productFinderSlice.reducer;
